import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
    LayoutDashboard, Layers, Building2, Banknote, Users, Receipt,
    ShieldAlert, Sparkles, FileBarChart2, Mail, GitMerge, Activity,
    LineChart, StickyNote, Plug, ShieldCheck, Cog, ChevronDown,
} from "lucide-react";

const STORAGE_KEY = "acc.sidebar.collapsed.v1";

const GROUPS = [
    {
        key: "financial",
        label: "Financial",
        items: [
            { to: "/", label: "Overview", icon: LayoutDashboard, exact: true },
            { to: "/group", label: "Group View", icon: Layers },
            { to: "/entities", label: "Entities", icon: Building2 },
            { to: "/cashflow", label: "Cashflow", icon: Banknote },
            { to: "/forecast", label: "Forecast", icon: LineChart },
            { to: "/debtors", label: "Debtors", icon: Users },
            { to: "/payables", label: "Payables", icon: Receipt },
            { to: "/reconciliation", label: "Reconciliation", icon: GitMerge },
        ],
    },
    {
        key: "intelligence",
        label: "Intelligence",
        items: [
            { to: "/ai-expert", label: "AI Expert", icon: Sparkles },
            { to: "/patterns", label: "Patterns", icon: Activity },
            { to: "/audit", label: "Audit findings", icon: ShieldAlert },
            { to: "/notes", label: "Management notes", icon: StickyNote },
            { to: "/reports", label: "Reports", icon: FileBarChart2 },
        ],
    },
    {
        key: "operations",
        label: "Operations",
        items: [
            { to: "/briefings", label: "Briefings", icon: Mail },
            { to: "/connections", label: "Connections", icon: Plug },
        ],
    },
    {
        key: "admin",
        label: "Admin",
        items: [
            { to: "/production-readiness", label: "Production readiness", icon: ShieldCheck },
            { to: "/settings", label: "Settings", icon: Cog },
        ],
    },
];

function loadCollapsed() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        return JSON.parse(raw);
    } catch {
        return {};
    }
}

function saveCollapsed(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        /* ignore */
    }
}

export default function Sidebar() {
    const [collapsed, setCollapsed] = useState(loadCollapsed);

    useEffect(() => {
        saveCollapsed(collapsed);
    }, [collapsed]);

    const toggle = (key) =>
        setCollapsed((c) => ({ ...c, [key]: !c[key] }));

    return (
        <aside
            className="w-64 fixed top-0 left-0 h-screen bg-[#18181b] text-[#f4f4f5] flex flex-col border-r border-[#27272a] z-50"
            data-testid="app-sidebar"
        >
            <div className="px-5 py-5 border-b border-[#27272a]">
                <div className="flex items-center gap-3">
                    <img
                        src="/all-crane-logo-sm.png"
                        alt="All Crane Sales"
                        className="h-10 w-auto"
                        style={{ filter: "brightness(0) invert(1)" }}
                        data-testid="brand-logo"
                    />
                    <div className="leading-tight">
                        <div
                            className="text-[15px] font-medium tracking-tight"
                            style={{ fontFamily: "Fraunces, serif" }}
                        >
                            All Crane
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                            Command Centre
                        </div>
                    </div>
                </div>
            </div>

            <nav
                className="flex-1 overflow-y-auto thin-scroll py-3"
                data-testid="sidebar-nav"
            >
                {GROUPS.map((group) => {
                    const isCollapsed = !!collapsed[group.key];
                    return (
                        <div key={group.key} className="mb-1.5">
                            <button
                                type="button"
                                className="nav-group-header w-full"
                                onClick={() => toggle(group.key)}
                                aria-expanded={!isCollapsed}
                                data-testid={`sidebar-group-${group.key}`}
                            >
                                <span>{group.label}</span>
                                <ChevronDown
                                    size={12}
                                    strokeWidth={2.5}
                                    className={`transition-transform duration-200 ${
                                        isCollapsed ? "-rotate-90" : "rotate-0"
                                    }`}
                                />
                            </button>
                            <div
                                className="disclosure-content"
                                style={{
                                    maxHeight: isCollapsed ? 0 : `${group.items.length * 40 + 20}px`,
                                    opacity: isCollapsed ? 0 : 1,
                                }}
                                aria-hidden={isCollapsed}
                            >
                                <div className="pb-2">
                                    {group.items.map((item) => (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            end={item.exact}
                                            className={({ isActive }) =>
                                                `nav-link ${isActive ? "active" : ""}`
                                            }
                                            data-testid={`nav-${item.label
                                                .toLowerCase()
                                                .replace(/\s+/g, "-")}`}
                                        >
                                            <item.icon
                                                size={15}
                                                strokeWidth={1.75}
                                                className="opacity-80"
                                            />
                                            <span>{item.label}</span>
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </nav>

            <div className="px-5 py-4 border-t border-[#27272a] text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <div>All Crane Command Centre</div>
                <div className="mt-1 text-zinc-600 normal-case tracking-normal">
                    Demo data · Connect MYOB to go live
                </div>
            </div>
        </aside>
    );
}
